import { LuIcon, Menu, Text } from "@overmap-ai/blocks"
import type { ProcedureInitiative, Stored } from "@overmap-ai/common"
import { gray } from "@radix-ui/colors"
import { FlagOff } from "lucide-react"
import { use, useCallback, useMemo } from "react"

import { LuIconWithWrapper } from "../../../components/Icons/LuIconWithWrapper"
import { ProcedureTypeIcon } from "../../../components/Icons/ProcedureTypeIcon"
import { ProcedureInitiativeIndexLabel } from "../../../components/IndexLabels/ProcedureInitiativeIndexLabel"
import { MultiSelectItemTemplate, SelectItemTemplate } from "../../../components/MenuGroups/constants"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProcedureInitiativeFilterDef } from "./ProcedureInitiativeFilterDef"

type ProcedureInitiativeFilterCellProps = FilterDefRenderProps<ProcedureInitiativeFilterDef>

export const ProcedureInitiativeFilterCell = (props: ProcedureInitiativeFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)
	const procedureInitiatives = filterDef.procedureInitiatives
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedProcedureInitiative = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return filterValue !== null ? procedureInitiatives.find(({ uuid }) => uuid === filterValue) : null
	}, [procedureInitiatives, filterValue, filterDef.modifiers.equals])

	const selectedProcedureInitiatives = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined

		const set = new Set(filterValue)
		return [...procedureInitiatives, null].filter((procedureInitiativeOrNull) =>
			set.has(procedureInitiativeOrNull?.uuid ?? null),
		)
	}, [procedureInitiatives, filterValue, filterDef.modifiers.includes])

	const handleValueSelect = useCallback(
		(procedureInitiativeOrNull?: Stored<ProcedureInitiative> | null) => {
			switch (procedureInitiativeOrNull) {
				case undefined:
				case null:
					filter.setFilterValue(procedureInitiativeOrNull)
					break
				default:
					filter.setFilterValue(procedureInitiativeOrNull.uuid)
					break
			}
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(procedureInitiativesAndNull: (Stored<ProcedureInitiative> | null)[]) => {
			if (procedureInitiativesAndNull.length > 0) {
				filter.setFilterValue(
					procedureInitiativesAndNull.map(
						(procedureInitiaiveOrNull) => procedureInitiaiveOrNull?.uuid ?? null,
					),
				)
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getProcedureInitiativeLabelUi = (procedureInitiativeOrNull: Stored<ProcedureInitiative> | null) => {
		return procedureInitiativeOrNull !== null ? (
			<>
				<ProcedureTypeIcon procedureTypeId={procedureInitiativeOrNull.procedure_type} />
				<ProcedureInitiativeIndexLabel procedureInitiativeId={procedureInitiativeOrNull.uuid} />
			</>
		) : (
			<>
				<LuIconWithWrapper icon={FlagOff} color={gray.gray9} />
				<Text className="truncate">None</Text>
			</>
		)
	}

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="shrink-1 min-w-0 grow">
								{selectedProcedureInitiative !== undefined
									? getProcedureInitiativeLabelUi(selectedProcedureInitiative)
									: "..."}
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<Menu.SelectGroup<Stored<ProcedureInitiative> | null>
									required={false}
									value={selectedProcedureInitiative}
									onValueChange={handleValueSelect}
								>
									{procedureInitiatives.map((procedureInitiative) => (
										<SelectItemTemplate<Stored<ProcedureInitiative> | null>
											key={procedureInitiative.uuid}
											value={procedureInitiative}
										>
											<ProcedureTypeIcon
												procedureTypeId={procedureInitiative.procedure_type}
												inline
											/>
											<ProcedureInitiativeIndexLabel
												procedureInitiativeId={procedureInitiative.uuid}
											/>
										</SelectItemTemplate>
									))}
									<SelectItemTemplate<Stored<ProcedureInitiative> | null> value={null}>
										<LuIconWithWrapper icon={FlagOff} background color={gray.gray9} inline />
										<Text className="truncate">None</Text>
									</SelectItemTemplate>
								</Menu.SelectGroup>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedProcedureInitiatives ?? []).length) {
						case 0:
							return "..."
						case 1:
							return getProcedureInitiativeLabelUi(selectedProcedureInitiatives![0])
						default:
							return (
								<span className="flex items-center gap-1 truncate">
									{selectedProcedureInitiatives!
										.slice(0, 3)
										.map((procedureInitiativeOrNull, index) =>
											procedureInitiativeOrNull !== null ? (
												<ProcedureTypeIcon
													key={procedureInitiativeOrNull.uuid}
													procedureTypeId={procedureInitiativeOrNull.procedure_type}
												/>
											) : (
												<LuIconWithWrapper
													key={`none-${index}`}
													icon={FlagOff}
													color={gray.gray9}
												/>
											),
										)}
									<Text className="truncate">{`${selectedProcedureInitiatives!.length} initiatives`}</Text>
								</span>
							)
					}
				}

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="shrink-1 min-w-0 grow">{getLabelUi()}</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<Menu.MultiSelectGroup<Stored<ProcedureInitiative> | null>
									values={selectedProcedureInitiatives}
									onValuesChange={handleValuesChange}
								>
									{procedureInitiatives.map((procedureInitiative) => (
										<MultiSelectItemTemplate<Stored<ProcedureInitiative> | null>
											key={procedureInitiative.uuid}
											value={procedureInitiative}
										>
											<ProcedureTypeIcon
												procedureTypeId={procedureInitiative.procedure_type}
												inline
											/>
											<ProcedureInitiativeIndexLabel
												procedureInitiativeId={procedureInitiative.uuid}
											/>
										</MultiSelectItemTemplate>
									))}
									<MultiSelectItemTemplate<Stored<ProcedureInitiative> | null> value={null}>
										<LuIconWithWrapper icon={FlagOff} background color={gray.gray9} inline />
										<Text className="truncate">None</Text>
									</MultiSelectItemTemplate>
								</Menu.MultiSelectGroup>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.procedureInitiave} />
				Initiative
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
